/**
 * Libraries
 */

import { defineStore } from 'pinia';

/**
 * Core
 */

import apolloClient from '@core/apollo';

/**
 * Types
 */

import type { LifecycleKey } from '@/modules/tickets/utils/ticketLifecycle';

import {
  AuthTicketPermissionsDocument,
  type TicketPermission,
  type AuthTicketPermissionsQuery,
} from '@generatedTypes';

/**
 * `TicketLifecycleKey` and `Status | Step` are two generated enums over the same nine
 * values: indexing by value lets the compiler check they stay aligned.
 */
type LifecycleKeyValue = `${LifecycleKey}`;

/** Row from `myTicketPermissions`: auth-scoped capabilities at one lifecycle key. */
type AuthTicketPermissionsRow = {
  lifecycle_key: LifecycleKeyValue;
  permissions: TicketPermission[];
};

type TicketPermissionsState = {
  byLifecycleKey: Partial<Record<LifecycleKeyValue, TicketPermission[]>>;
  loaded: boolean;
  loadPromise: Promise<void> | null;
  error: unknown;
};

const useTicketPermissionsStore = defineStore('ticketPermissions', {
  state: (): TicketPermissionsState => ({
    byLifecycleKey: {},
    loaded: false,
    loadPromise: null,
    error: null,
  }),

  getters: {
    permissionsFor:
      (state) =>
      (lifecycleKey: LifecycleKey): TicketPermission[] | undefined =>
        state.byLifecycleKey[lifecycleKey],
  },

  actions: {
    setMatrix(rows: readonly AuthTicketPermissionsRow[]) {
      const byLifecycleKey: Partial<
        Record<LifecycleKeyValue, TicketPermission[]>
      > = {};

      for (const row of rows) {
        byLifecycleKey[row.lifecycle_key] = row.permissions;
      }

      this.byLifecycleKey = byLifecycleKey;
      this.loaded = true;
      this.error = null;
    },

    reset() {
      this.byLifecycleKey = {};
      this.loaded = false;
      this.loadPromise = null;
      this.error = null;
    },

    load(): Promise<void> {
      if (this.loaded) {
        return Promise.resolve();
      }

      if (this.error) {
        return Promise.reject(this.error);
      }

      if (this.loadPromise) {
        return this.loadPromise;
      }

      this.loadPromise = apolloClient
        .query<AuthTicketPermissionsQuery>({
          query: AuthTicketPermissionsDocument,
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          this.setMatrix(data.myTicketPermissions ?? []);
        })
        .catch((error: unknown) => {
          this.error = error;
          throw error;
        })
        .finally(() => {
          this.loadPromise = null;
        });

      return this.loadPromise;
    },
  },
});

export { useTicketPermissionsStore };
